import type { Editor, JSONContent } from '@tiptap/core';
import type { Node as PMNode } from '@tiptap/pm/model';
import { NodeSelection, TextSelection } from '@tiptap/pm/state';
import { closeHistory } from '@tiptap/pm/history';
import type { PickedProduct } from '@/components/products/productPickerSelection';
import { formatCurrency } from '@/lib/format';
import type { PricingType } from '@/theme/uboTheme';
import {
  calculateInvestmentProduct,
  createInvestmentProduct,
  investmentProductRow,
  type InvestmentProduct,
} from './investmentProduct';
import type { PublicProductPrice } from '../../../backend/src/utils/productInvestment';
import { investmentRowAmount } from './investmentRowAmount';
import { emptyTableRowMatchingTable } from './investmentTableRow';
import { buildInvestmentHeaderRowJson, getInvestmentTableLabels } from './investmentTableLabels';
import { buildNewPhaseNodes, cloneInvestmentPhaseNodes } from './investmentPhaseActions';
import { nanoid } from 'nanoid';

export const isQuoteWorkspacePreview = (editor: Editor) =>
  editor.view.dom.dataset.quoteWorkspace === 'preview';

/** Move live page nodes together, preserving pending edits, selection and undo history. */
export function reorderQuotePages(editor: Editor, blockIds: string[]): boolean {
  const nodes: Array<{ id: string; node: PMNode; pos: number }> = [];
  editor.state.doc.forEach((node, pos) => nodes.push({ id: String(node.attrs.id || ''), node, pos }));
  // StarterKit adds an untracked trailing paragraph after page nodes. Preserve it
  // (and any other untracked content) while ordering only the sidebar's pages.
  const pages = nodes.filter((entry) => entry.id);
  const byId = new Map(pages.map((page) => [page.id, page]));
  if (blockIds.length !== pages.length || new Set(blockIds).size !== pages.length ||
      blockIds.some((id) => !id || !byId.has(id))) {
    throw new Error('De paginalijst is gewijzigd. Probeer de pagina opnieuw te verplaatsen.');
  }
  if (pages.every((page, index) => page.id === blockIds[index])) return false;

  let pageIndex = 0;
  const reordered = nodes.map((entry) => entry.id ? byId.get(blockIds[pageIndex++])! : entry);
  const nextPositions = new Map<number, number>();
  let offset = 0;
  for (const page of reordered) {
    nextPositions.set(page.pos, offset);
    offset += page.node.nodeSize;
  }
  const mapPosition = (pos: number) => {
    const owner = nodes.find((entry) => pos >= entry.pos && pos < entry.pos + entry.node.nodeSize);
    return owner ? nextPositions.get(owner.pos)! + pos - owner.pos : offset;
  };
  const { selection } = editor.state;
  const tr = closeHistory(editor.state.tr).replaceWith(
    0, editor.state.doc.content.size, reordered.map((page) => page.node),
  );
  if (selection instanceof NodeSelection) {
    tr.setSelection(NodeSelection.create(tr.doc, mapPosition(selection.from)));
  } else if (selection instanceof TextSelection) {
    tr.setSelection(TextSelection.create(tr.doc, mapPosition(selection.anchor), mapPosition(selection.head)));
  }
  editor.view.dispatch(tr);
  // Subsequent typing is a separate operation from moving the page.
  editor.view.dispatch(closeHistory(editor.state.tr).setMeta('addToHistory', false));
  return true;
}

export function investmentGroupForPage(doc: PMNode, blockId: string | null) {
  const pages: Array<{ node: PMNode; pos: number }> = [];
  doc.forEach((node, pos) => pages.push({ node, pos }));
  return pages.find(
    ({ node }) => node.type.name === 'investmentGroupNode' && node.attrs.id === blockId
  );
}

/** Add an overview to the selected investment page, keeping its parent selected. */
export function addInvestmentPhaseToPage(editor: Editor, blockId: string): string {
  const group = investmentGroupForPage(editor.state.doc, blockId);
  if (!group) throw new Error('Deze investeringspagina bestaat niet meer.');
  const phases = investmentDestinations(editor.state.doc).filter((phase) => phase.groupId === blockId);
  let number = phases.length + 1;
  while (phases.some((phase) => phase.title === `Investeringsoverzicht ${number}`)) number++;
  const nodes = buildNewPhaseNodes(number, getInvestmentTableLabels(group.node.attrs.labelLanguage));
  const pos = group.pos + group.node.nodeSize - 1;
  const tr = closeHistory(editor.state.tr).insert(pos, nodes.map((node) => editor.schema.nodeFromJSON(node)));
  editor.view.dom.dataset.workspaceSelection = 'active';
  tr.setSelection(NodeSelection.create(tr.doc, group.pos));
  editor.view.dispatch(tr);
  editor.view.dispatch(closeHistory(editor.state.tr).setMeta('addToHistory', false));
  return nodes[0].attrs!.tableId;
}

/**
 * Copy an overview directly below the original, with a title of its own. The copy is selected,
 * so the panel and the outline show it; `keepPageSelected` keeps the overview list open instead.
 */
export function duplicateInvestmentPhase(
  editor: Editor,
  tableId: string,
  { keepPageSelected = false }: { keepPageSelected?: boolean } = {}
): { tableId: string; title: string } {
  const source = investmentDestinations(editor.state.doc).find((phase) => phase.tableId === tableId);
  if (!source) throw new Error('Dit investeringsoverzicht bestaat niet meer.');
  const $header = editor.state.doc.resolve(source.headerPos);
  const group = $header.parent;
  const nodes: PMNode[] = [];
  let end = source.headerPos;
  for (let index = $header.index(); index < group.childCount; index++) {
    const node = group.child(index);
    if (nodes.length && node.type.name === 'investmentHeaderNode') break;
    nodes.push(node);
    end += node.nodeSize;
  }
  const titles: string[] = [];
  group.forEach((node) => {
    if (node.type.name === 'investmentHeaderNode') titles.push(String(node.attrs.phaseTitle ?? ''));
  });
  const copy = cloneInvestmentPhaseNodes(
    nodes.map((node) => node.toJSON() as JSONContent),
    `phase-${Date.now()}-${nanoid(8)}`,
    titles
  ).map((json) => editor.schema.nodeFromJSON(json));
  const tr = closeHistory(editor.state.tr).insert(end, copy);
  editor.view.dom.dataset.workspaceSelection = 'active';
  tr.setSelection(NodeSelection.create(tr.doc, keepPageSelected ? $header.before() : end));
  editor.view.dispatch(tr);
  editor.view.dispatch(closeHistory(editor.state.tr).setMeta('addToHistory', false));
  return { tableId: String(copy[0].attrs.tableId), title: String(copy[0].attrs.phaseTitle) };
}

/** Scroll an overview into view once its node view has rendered. */
export function revealInvestmentPhase(editor: Editor, tableId: string) {
  requestAnimationFrame(() => {
    editor.view.dom
      .querySelector(`[data-investment-table-id="${CSS.escape(tableId)}"]`)
      ?.scrollIntoView({ block: 'center' });
  });
}

/** Move complete live overview sections; the main header and page attributes stay in place. */
export function reorderInvestmentPhases(editor: Editor, blockId: string, tableIds: string[]): boolean {
  const group = investmentGroupForPage(editor.state.doc, blockId);
  if (!group) throw new Error('Deze investeringspagina bestaat niet meer.');
  const prefix: PMNode[] = [];
  const phases: Array<{ id: string; nodes: PMNode[] }> = [];
  group.node.forEach((node) => {
    if (node.type.name === 'investmentHeaderNode' && !node.attrs.isMainHeader) {
      phases.push({ id: String(node.attrs.tableId || ''), nodes: [] });
    }
    const phase = phases.at(-1);
    (phase ? phase.nodes : prefix).push(node);
  });
  const byId = new Map(phases.map((phase) => [phase.id, phase]));
  if (tableIds.length !== phases.length || byId.size !== phases.length ||
      new Set(tableIds).size !== phases.length || tableIds.some((id) => !id || !byId.has(id))) {
    throw new Error('De lijst met investeringsoverzichten is gewijzigd. Probeer opnieuw te sorteren.');
  }
  if (phases.every((phase, index) => phase.id === tableIds[index])) return false;
  const tr = closeHistory(editor.state.tr).replaceWith(
    group.pos + 1, group.pos + group.node.nodeSize - 1,
    [...prefix, ...tableIds.flatMap((id) => byId.get(id)!.nodes)],
  );
  editor.view.dom.dataset.workspaceSelection = 'active';
  tr.setSelection(NodeSelection.create(tr.doc, group.pos));
  editor.view.dispatch(tr);
  editor.view.dispatch(closeHistory(editor.state.tr).setMeta('addToHistory', false));
  return true;
}

export interface InvestmentDestination {
  tableId: string;
  groupId: string;
  title: string;
  pricingType: PricingType;
  currency: string;
  language: 'nl' | 'en';
  headerPos: number;
  tablePos: number;
  totalPos: number;
}

export function investmentPhaseRows(doc: PMNode, destination: InvestmentDestination) {
  const rows: Array<{
    pos: number;
    rowPos: number;
    index: number;
    cells: string[];
    rateKey?: string;
    showHours?: boolean;
    title: string;
    product?: InvestmentProduct;
    productId?: string;
    productVersionId?: string;
    variantId: string | null;
    quantity: number | null;
    discountPct: number | null;
    hasOverrides: boolean;
    amount: number | null;
    /** Bovenkant van een van–tot prijs; gelijk aan amount zonder bandbreedte. */
    maxAmount: number | null;
  }> = [];
  doc.nodeAt(destination.tablePos)?.forEach((row, offset) => {
    if (row.firstChild?.type.name === 'tableHeader') return;
    const product = row.attrs.investmentProduct as InvestmentProduct | undefined;
    const cells: string[] = [];
    row.forEach((cell) => cells.push(cell.textContent));
    const rowPos = destination.tablePos + 1 + offset;
    const computed = product ? calculateInvestmentProduct(product) : null;
    const price = row.attrs.productPrice as PublicProductPrice | undefined;
    const amount = computed
      ? computed.minCents
      : price
        ? price.totalCents
        : destination.pricingType === 'fixed'
          ? 0
          : Math.round(investmentRowAmount(cells, destination.pricingType === 'hourly') * 100);
    rows.push({
      pos: rowPos + 3,
      rowPos,
      index: rows.length + 1,
      cells,
      rateKey: row.attrs.rateKey ?? undefined,
      showHours: row.attrs.showHours ?? undefined,
      title: product?.productName || cells[0] || 'Lege regel',
      product,
      productId: product?.productId ?? row.attrs.productId,
      productVersionId: product?.productVersionId ?? row.attrs.productVersionId,
      variantId: row.attrs.variantId ?? null,
      quantity: row.attrs.quantity ?? null,
      discountPct: row.attrs.discountPct ?? null,
      hasOverrides: row.attrs.hasOverrides === true,
      amount,
      maxAmount: computed ? computed.maxCents : (price?.maxTotalCents ?? amount),
    });
  });
  return rows;
}

/** Resolve each edit against the live table; typing changes document positions. */
export function findInvestmentPhaseRow(doc: PMNode, tableId: string, index: number): { row: PMNode; pos: number } | null {
  const destination = investmentDestinations(doc).find((phase) => phase.tableId === tableId);
  if (!destination) return null;
  let current = 0;
  let result: { row: PMNode; pos: number } | null = null;
  doc.nodeAt(destination.tablePos)?.forEach((row, offset) => {
    if (row.firstChild?.type.name === 'tableHeader') return;
    if (++current === index) result = { row, pos: destination.tablePos + 1 + offset };
  });
  return result;
}

/**
 * Move one data row of a phase table; `from` and `to` are 1-based like `index` in
 * `investmentPhaseRows`. A cursor inside the table stays in the row it was in, so the
 * inspector keeps showing the same row.
 */
export function moveInvestmentPhaseRow(editor: Editor, tableId: string, from: number, to: number): boolean {
  const destination = investmentDestinations(editor.state.doc).find((phase) => phase.tableId === tableId);
  const table = destination && editor.state.doc.nodeAt(destination.tablePos);
  if (!destination || !table) return false;
  const rows: Array<{ node: PMNode; pos: number }> = [];
  table.forEach((row, offset) => {
    if (row.firstChild?.type.name !== 'tableHeader') rows.push({ node: row, pos: destination.tablePos + 1 + offset });
  });
  if (from === to || !rows[from - 1] || !rows[to - 1]) return false;
  const ordered = [...rows];
  const [moved] = ordered.splice(from - 1, 1);
  ordered.splice(to - 1, 0, moved);

  const start = rows[0].pos;
  const { selection } = editor.state;
  const selected = rows.find((row) => selection.from > row.pos && selection.from < row.pos + row.node.nodeSize);
  const tr = closeHistory(editor.state.tr).replaceWith(
    start, rows[rows.length - 1].pos + rows[rows.length - 1].node.nodeSize,
    ordered.map((row) => row.node),
  );
  if (selected) {
    let pos = start;
    for (const row of ordered) {
      if (row === selected) break;
      pos += row.node.nodeSize;
    }
    tr.setSelection(TextSelection.near(tr.doc.resolve(pos + selection.from - selected.pos)));
  }
  editor.view.dispatch(tr);
  editor.view.dispatch(closeHistory(editor.state.tr).setMeta('addToHistory', false));
  return true;
}

export function investmentPhaseTotal(doc: PMNode, destination: InvestmentDestination) {
  const total = doc.nodeAt(destination.totalPos);
  const rows = investmentPhaseRows(doc, destination);
  const amount = rows.reduce((sum, row) => sum + (row.amount ?? 0), 0);
  const maxAmount = rows.reduce((sum, row) => sum + (row.maxAmount ?? 0), 0);
  const fixed = destination.pricingType === 'fixed';
  const fixedTotal = fixed ? Math.round((Number(total?.attrs.fixedTotal) || 0) * 100) : 0;
  let hasFixedProducts = false;
  doc.nodeAt(destination.tablePos)?.forEach((row) => {
    if (row.attrs.investmentProduct || row.attrs.productPrice) hasFixedProducts = true;
  });
  return {
    amount: amount + fixedTotal,
    maxAmount: maxAmount + fixedTotal,
    text: fixed && !hasFixedProducts ? String(total?.attrs.fixedTotalText || '') : '',
    hasOnRequest: rows.some((row) => row.amount === null),
  };
}

/** Add a standalone quote row using the phase's existing columns and currency. */
export function addInvestmentRowToPhase(editor: Editor, tableId: string) {
  const destination = investmentDestinations(editor.state.doc).find((phase) => phase.tableId === tableId);
  if (!destination) throw new Error('Selecteer een bestaande fase om een regel toe te voegen.');
  const table = editor.state.doc.nodeAt(destination.tablePos)!;
  const pos = destination.tablePos + table.nodeSize - 1;
  const row = editor.schema.nodeFromJSON(
    emptyTableRowMatchingTable(table, destination.pricingType, destination.currency),
  );
  const tr = closeHistory(editor.state.tr).insert(pos, row);
  tr.setSelection(TextSelection.near(tr.doc.resolve(pos + 3)));
  editor.view.dom.dataset.workspaceSelection = 'active';
  editor.view.dispatch(tr);
  return pos;
}

/** Lees de bestemmingen telkens uit het document; posities veranderen tijdens bewerken. */
export function investmentDestinations(doc: PMNode): InvestmentDestination[] {
  const result: InvestmentDestination[] = [];
  doc.descendants((group, groupPos) => {
    if (group.type.name !== 'investmentGroupNode') return true;
    let phase: Omit<InvestmentDestination, 'totalPos'> | null = null;
    group.forEach((node, offset) => {
      const pos = groupPos + 1 + offset;
      if (node.type.name === 'investmentHeaderNode' && !node.attrs.isMainHeader) {
        phase = {
          tableId: String(node.attrs.tableId),
          groupId: String(group.attrs.id),
          title: String(node.attrs.phaseTitle || 'Naamloze fase'),
          pricingType: node.attrs.pricingType || 'fixed',
          currency: group.attrs.currency || 'EUR',
          language: group.attrs.labelLanguage === 'en' ? 'en' : 'nl',
          headerPos: pos,
          tablePos: -1,
        };
      } else if (node.type.name === 'table' && phase) {
        phase.tablePos = pos;
      } else if (node.type.name === 'investmentTotalNode' && phase && phase.tablePos >= 0) {
        result.push({ ...phase, totalPos: pos });
        phase = null;
      }
    });
    return false;
  });
  return result;
}

function preservedHourlyRows(
  table: PMNode,
  type: PricingType,
  fixedTotal: number,
  currency: string,
  fixedText: string
): JSONContent[] {
  const rows: JSONContent[] = [];
  let assigned = false;
  const cell = (text: string): JSONContent => ({
    type: 'tableCell',
    content: [{ type: 'paragraph', ...(text ? { content: [{ type: 'text', text }] } : {}) }],
  });
  table.forEach((row) => {
    if (row.firstChild?.type.name === 'tableHeader') return;
    if (row.attrs.investmentProduct) {
      rows.push(investmentProductRow(row.attrs.investmentProduct, 'hourly', currency));
      return;
    }
    const cells: PMNode[] = [];
    row.forEach((entry) => cells.push(entry));
    if (!cells.some((entry) => entry.textContent.trim()) && !row.attrs.productId) return;
    let amount = type === 'recurring' ? cells[1]?.textContent || '' : '';
    if (type === 'fixed' && !assigned && (fixedTotal || fixedText)) {
      amount = fixedText || formatCurrency(fixedTotal, currency);
      assigned = true;
    }
    rows.push({
      type: 'tableRow',
      attrs: { ...row.attrs },
      content: [cells[0].toJSON(), cell(amount), cell(amount ? '1' : ''), cell(amount)],
    });
  });
  if (type === 'fixed' && (fixedTotal || fixedText) && !assigned) {
    const amount = fixedText || formatCurrency(fixedTotal, currency);
    rows.push({
      type: 'tableRow',
      content: [cell('Bestaand vast bedrag'), cell(amount), cell('1'), cell(amount)],
    });
  }
  return rows;
}

/** De hele selectie wordt één transactie, dus één undo. Bestaande fase-inhoud blijft behouden. */
export function addProductsToPhase(
  editor: Editor,
  tableId: string,
  items: PickedProduct[]
): string[] {
  const destination = investmentDestinations(editor.state.doc).find(
    (entry) => entry.tableId === tableId
  );
  if (!destination || !items.length)
    throw new Error('Selecteer een bestaande fase om producten toe te voegen.');
  const { tablePos, headerPos, totalPos, pricingType, currency, language } = destination;
  const table = editor.state.doc.nodeAt(tablePos)!;
  const products = items.map(({ snapshot, options }) => createInvestmentProduct(snapshot, options));
  const productRows = products.map((product) => investmentProductRow(product, 'hourly', currency));
  const tr = closeHistory(editor.state.tr);
  if (pricingType === 'hourly') {
    tr.insert(
      tablePos + table.nodeSize - 1,
      productRows.map((row) => editor.schema.nodeFromJSON(row))
    );
  } else {
    const header = editor.state.doc.nodeAt(headerPos)!;
    const total = editor.state.doc.nodeAt(totalPos)!;
    const rows = preservedHourlyRows(
      table,
      pricingType,
      Number(total.attrs.fixedTotal) || 0,
      currency,
      String(total.attrs.fixedTotalText || '')
    );
    const nextTable = editor.schema.nodeFromJSON({
      type: 'table',
      attrs: table.attrs,
      content: [
        buildInvestmentHeaderRowJson('hourly', getInvestmentTableLabels(language)),
        ...rows,
        ...productRows,
      ],
    });
    tr.setNodeMarkup(headerPos, undefined, {
      ...header.attrs,
      pricingType: 'hourly',
      showHourColumns: false,
    });
    tr.replaceWith(tablePos, tablePos + table.nodeSize, nextTable);
    tr.setNodeMarkup(tr.mapping.map(totalPos), undefined, {
      ...total.attrs,
      pricingType: 'hourly',
      fixedTotal: 0,
      fixedTotalText: null,
    });
  }
  editor.view.dispatch(tr);
  return products.map((product) => product.id);
}

export function selectWorkspacePosition(editor: Editor, pos: number) {
  editor.view.dom.dataset.workspaceSelection = pos > 0 ? 'active' : 'none';
  editor.view.dispatch(
    editor.state.tr.setSelection(TextSelection.near(editor.state.doc.resolve(pos)))
  );
}
